<template>
  <v-card class="pa-4" flat>
    <div class="d-flex justify-space-between align-center mb-4">
      <v-btn
        @click="openAddDialog"
        outlined
        class="pa-2 btn-get-code rounded-lg"
      >
        <v-icon left>mdi-plus</v-icon>
        افزودن زمانبندی اجرا
      </v-btn>
    </div>

    <v-progress-linear v-show="loading" class="mb-2" indeterminate />

    <custom-popup
      v-model="showDialog"
      :cons="{ title: isEditing ? 'ویرایش زمانبندی' : 'افزودن زمانبندی' }"
      max-width="500"
      :reloadable="false"
      :persistent="true"
      @close="resetForm"
    >
      <v-card flat class="pa-4" elevation="0">
        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="pa-5 border rounded-xl" style="background: #f9f9f9">
            <div class="d-flex align-center mb-6">
              <v-icon color="primary" class="ml-2">mdi-cog-outline</v-icon>
              <span class="text-h6">تنظیمات زمان‌بندی</span>
            </div>
            
            <timepicker
              v-model="formData.time"
              label="زمان اجرای اتوماتیک (روزانه)"
              prepend-icon="mdi-alarm"
              class="mb-4"
            />
            
            <v-switch 
              label="وضعیت فعالیت" 
              v-model="formData.is_active" 
              hide-details
              class="mb-6"
            ></v-switch>

            <div class="d-flex flex-row justify-space-between mt-6">
              <v-btn
                class="btn-get-code rounded-lg"
                @click.stop="saveSchedule"
                :loading="scheduleLoading"
              >
                <v-icon left>mdi-content-save</v-icon>
                ذخیره
              </v-btn>
              <v-btn 
                class="btn-delete rounded-lg" 
                @click.stop="showDialog = false"
              >
                انصراف
              </v-btn>
            </div>
          </div>
        </v-form>
      </v-card>
    </custom-popup>

    <vue-table
      :data="tableData"
      :headers="headers"
      :loading="syncLoading || loading"
      @change="onPagination"
      class="w-100"
    >
      <template v-slot:filters="{ props, perPage }">
        <v-row no-gutters class="mb-4">
          <v-col class="pa-2" cols="12" lg="2" md="3">
            <v-select
              v-model="props.length"
              :items="perPage"
              class="rounded-lg"
              dense
              hide-details
              :label="$t('qrUsersList.perPage')"
              outlined
              @change="() => onPagination(props)"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:index="{ item }">
        {{ persian(item.index + 1 + (tableData.current_page - 1) * tableData.per_page) }}
      </template>

      <!-- سوییچ وضعیت برای جدول -->
      <template v-slot:is_active="{ item }">
        <div class="d-flex justify-center">
          <v-switch
            v-model="item.is_active"
            @change="toggleStatus(item)"
            hide-details
            class="mt-0 pt-0"
            color="primary"
          ></v-switch>
        </div>
      </template>

      <template v-slot:image_src="{ item }">
        <div class="d-flex flex-column align-center pa-2">
          <v-img :src="item.image_src" max-width="115" max-height="115" contain />
        </div>
      </template>

      <template v-slot:kala="{ item }">
        <div class="d-flex flex-column align-start text-right py-2">
          <div class="product-title font-weight-bold mb-1">
            <template v-if="item.title && item.title.includes(':')">
              <div>{{ item.title.split(':')[0] }} :</div>
              <div class="primary--text mt-1">{{ item.title.split(':').slice(1).join(':') }}</div>
            </template>
            <template v-else>
              {{ item.title }}
            </template>
          </div>
          <div class="d-flex align-center gap-2 grey--text text--darken-1 text-caption">
            <a :href="`https://www.digikala.com/product/dkp-${item.product_id}`" target="_blank" class="dkp-link">
              DKP-{{ item.product_id }}
            </a>
            <span class="mx-1">|</span>
            <span>{{ item.product_id }}</span>

            <span class="mx-1">|</span>
            <span class="variant-code">DKPC-{{ item.variant_id }}</span>
          </div>
        </div>
      </template>
      
      <template v-slot:variant_id="{ item }">
        <v-chip class="px-2 py-3 rounded-lg" color="transparent">
          <span class="text-body-2 font-weight-bold">
            {{ persian(item.variant_id) }}
          </span>
          <v-btn icon small @click.stop="copyID(item.variant_id)" class="ms-2" color="#B5C5FD">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </v-chip>
      </template>

      <template v-slot:updated_at="{ item }">
        <v-icon small class="ml-1">mdi-clock-outline</v-icon>
        {{ jalalian(item.updated_at, "datetime") }}
      </template>

      <template v-slot:flag="{ item }">
        <v-chip :color="getFlagColor(item.flag)" text-color="white" small class="pa-2">
          {{ getFlagText(item.flag) }}
        </v-chip>
      </template>

      <template v-slot:action="{ item }">
        <action-buttons :data="item" :meta="metabutton" />
      </template>
    </vue-table>
  </v-card>
</template>

<script>
import Timepicker from "@/view/components/Timepicker";
import ActionButtons from "@/view/components/adapters/qrList/ordersListButton.vue";


export default {
  components: { Timepicker, ActionButtons },
  data() {
    return {
      valid: false,
      formData: {
        id: null,
        time: null,
        type: 'daily',
        is_active: true,
      },
      defaultForm: {
        id: null,
        time: null,
        type: 'daily',
        is_active: true,
      },
      isEditing: false,
      tableProps: {
        page: 1,
        length: 10,
      },
      tableData: {
        data: [],
        total: 0,
        current_page: 1,
        per_page: 10
      },
      loading: false,
      syncLoading: false,
      repriceLoading: false,
      scheduleLoading: false,
      showDialog: false,
      headers: [
        { value: 'index', text: 'ردیف', sortable: false, align: 'center' },
        { value: 'time', text: 'زمان اجرا', sortable: false, align: 'center' },
        { value: 'is_active', text: 'وضعیت', sortable: false, align: 'center' },
        { value: "action", text: "عملیات", align: "center", sortable: false, event: "actions" },
      ],
    }
  },
  mounted() {
    this.fetch();
  },
  computed: {
    metabutton() {
      return {
        buttons: [
          {
            name: "edit",
            label: "ویرایش",
            icon: "pencil",
            class: "btn-edit rounded-pill",
            action: this.actions,
          },
          {
            name: "delete",
            label: "حذف",
            icon: "delete",
            class: "btn-delete rounded-pill",
            action: this.actions,
          },
        ],
      };
    },
  },
  methods: {
    openAddDialog() {
      this.isEditing = false;
      this.formData = { ...this.defaultForm };
      this.showDialog = true;
    },
    resetForm() {
      this.formData = { ...this.defaultForm };
      this.isEditing = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    async fetch() {
      this.loading = true;
      try {
        const res = await this.$api.get('/v1/digikala/schedule', { params: this.tableProps });
        this.tableData = res.data.data || res.data;
      } catch (error) {
        this.$toast.error("خطا در دریافت لیست زمانبندی");
      } finally {
        this.loading = false;
      }
    },
    onPagination(e) {
      if (e && typeof e === 'object' && !Array.isArray(e)) {
        this.tableProps = { ...this.tableProps, ...e };
      }
      this.fetch();
    },
    actions(data, type) {
      if (type === "edit") {
        this.editAction(data);
      } else if (type === "delete") {
        this.deleteAction(data);
      }
    },
    editAction(data) {
      this.isEditing = true;
      this.formData = { ...data };
      this.showDialog = true;
    },
    async deleteAction(data) {
      const confirm = await this.$modal.yesNo("آیا از حذف این مورد اطمینان دارید؟");
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.delete(`/v1/digikala/schedule/${data.id}`);
        this.$toast.success("با موفقیت حذف شد");
        await this.fetch();
      } catch {
        this.$toast.error("خطا در حذف اطلاعات");
        this.loading = false;
      }
    },
       async saveSchedule() {
      if (!this.formData.time) {
        this.$toast.error("لطفاً زمان را انتخاب کنید");
        return;
      }
      
      let url = !this.isEditing ? '/v1/digikala/schedule/store' : `/v1/digikala/schedule/${this.formData.id}`;
      let method = !this.isEditing ? 'post' : 'put';
      
      let formattedTime = this.formData.time?.split(':').slice(0, 2).join(':');
      
      this.scheduleLoading = true;
      try {
        await this.$api[method](url, {
          time: formattedTime,
          type: "daily",
          is_active: this.formData.is_active,
        });
        
        this.$toast.success("زمان‌بندی با موفقیت ذخیره شد");
        this.showDialog = false;
        await this.fetch();
      } catch (error) {
        this.$toast.error("خطا در ذخیره زمان‌بندی");
      } finally {
        this.scheduleLoading = false;
      }
    },
    
    async toggleStatus(item) {
      let formattedTime = item.time.split(':').slice(0, 2).join(':');
      
      try {
        await this.$api.put(`/v1/digikala/schedule/${item.id}`, {
          time: formattedTime,
          type: item.type || "daily",
          is_active: item.is_active?1:0, 
        });
        this.$toast.success("وضعیت با موفقیت به‌روزرسانی شد");
      } catch (error) {
        item.is_active = !item.is_active;
        this.$toast.error("خطا در تغییر وضعیت");
      }
    },
 
  }
}
</script>

<style scoped>
.gap-4 {
  gap: 16px;
}
</style>
